/**
 * Code to notify the UI of API and UI version mismatches.
 */
import {BehaviorSubject} from 'rxjs';
import {BASE_API_URL} from '../envs.ts';

let cancelInterval: NodeJS.Timeout;

/**
 * Regularily fetch the API version.
 * When there is a change, notify the observers.
 */
export function initializeObsoleteApiWatchdog() {
  if (!cancelInterval) {
    checkApiVersion();
    cancelInterval = setInterval(() => {
      checkApiVersion();
    }, 3_600_000 * 24); // 1d
  }
}

let initialVersion: string;

/**
 * Subscribe to be notified when the application needs to be reloaded.
 * For example on UI/API version mismatch or dynamic import failure.
 */
export const needsReload$ = new BehaviorSubject({
  reload: false,
  reason: 'initial',
});

async function checkApiVersion() {
  const r = await fetch(`${BASE_API_URL}/api/6/version.txt`, {
    priority: 'low',
    credentials: 'omit',
  });
  if (r.ok) {
    const currentVersion = await r.text();
    if (!initialVersion) {
      initialVersion = currentVersion;
      return;
    }
    if (initialVersion !== currentVersion) {
      needsReload$.next({
        reload: true,
        reason: 'API version mismatch',
      });
    }
  }
}

/**
 * Monitor rejected promise and notify observable.
 * Wrap all dynamic imports with it to detect obsolete UI.
 * @param promise A dynamic import promise
 * @return the input promise
 */
export async function monitorLoadingError<T>(promise: Promise<T>): Promise<T> {
  Promise.allSettled([promise]).then((results) => {
    switch (results[0].status) {
      case 'fulfilled':
        // continuer
        break;
      case 'rejected':
        needsReload$.next({
          reload: true,
          reason: 'rejected promise',
        });
        break;
    }
  });
  return promise;
}
